<template>
  <div class="relative">
    <button
      ref="toggleButtonRef"
      type="button"
      style="height: 37px"
      class="px-4 rounded border border-[#CBD5E1] text-sm font-semibold text-[#475569] flex items-center gap-2 bg-white hover:border-[#94A3B8] focus:outline-none focus:border-[#3B82F6]"
      @click="togglePanel"
    >
      <span
        class="inline-flex h-4 w-4 items-center justify-center cursor-pointer"
      >
        <Icon icon-name="FilterIcon" />
      </span>
      <span class="ml-2 cursor-pointer text-left">Filters</span>
    </button>

    <transition name="fade">
      <div
        v-if="isOpen"
        ref="panelRef"
        class="absolute left-0 mt-2 rounded-xl border border-[#E2E8F0] bg-white shadow-xl z-50"
        style="width: 547px"
        @click.stop
        @mousedown.stop
      >
        <div class="px-6 pt-6 pb-4 text-left">
          <div class="text-black text-base font-medium mb-4 text-left">
            Filters
          </div>
          <div>
            <div
              v-for="(filter, index) in workingFilters"
              :key="filter.id"
              class="flex items-center gap-3"
              :class="{ 'mt-4': index > 0 }"
            >
              <Dropdown
                class-name="w-full"
                style="width: 160px"
                variant="simple"
                :options="filterDefinitions"
                :value="filter.field"
                :get-label="getFilterLabel"
                :get-value="getFilterKey"
                placeholder="Select Filter"
                @update:value="handleFieldSelect(index, $event)"
              />
              <template v-if="filter.field">
                <Dropdown
                  class-name="w-full"
                  style="width: 140px"
                  variant="simple"
                  :options="getOperatorsForFilter(filter.field)"
                  :value="filter.operator"
                  :get-label="getOperatorLabel"
                  :get-value="getOperatorValue"
                  placeholder="Select"
                  :disabled="!filter.field"
                  @update:value="handleOperatorSelect(index, $event)"
                />

                <div class="flex-1">
                  <template v-if="getValueType(filter.field) === 'select'">
                    <Dropdown
                      class-name="w-full"
                      variant="simple"
                      :options="getValueOptions(filter.field)"
                      :value="filter.value"
                      :get-label="getValueOptionLabel"
                      :get-value="getValueOptionValue"
                      placeholder="Select Value"
                      :disabled="!filter.operator"
                      @update:value="handleValueSelect(index, $event)"
                    />
                  </template>
                  <template v-else-if="getValueType(filter.field) === 'date'">
                    <input
                      type="date"
                      class="w-full h-10 rounded border border-[#CBD5E1] px-3 text-sm text-[#0F172A] focus:outline-none"
                      :value="filter.value || ''"
                      :disabled="!filter.operator"
                      @input="handleInputChangeEvent(index, $event)"
                      @click.stop
                      @mousedown.stop
                    />
                  </template>
                  <template v-else>
                    <input
                      type="text"
                      class="w-full h-10 rounded border border-[#CBD5E1] px-3 text-sm text-[#0F172A] focus:outline-none"
                      :value="filter.value || ''"
                      :disabled="!filter.operator"
                      @input="handleInputChangeEvent(index, $event)"
                      @click.stop
                      @mousedown.stop
                    />
                  </template>
                </div>
              </template>

              <div class="cursor-pointer" @click.stop="removeFilter(index)" @mousedown.stop>
                <span class="sr-only">Remove filter</span>
                <Icon icon-name="DeleteIcon" />
              </div>
            </div>

            <div
              class="mt-4 cursor-pointer inline-flex items-center gap-2 rounded border border-primary p-2 text-sm font-semibold text-primary"
              @click.stop="addFilter"
              @mousedown.stop
            >
              <span class="text-sm leading-none">+</span>
              <span>Add Filter</span>
            </div>
          </div>
        </div>
        <div class="flex justify-end gap-3 px-6 py-4 border-t border-[#E2E8F0]">
          <div
            class="cursor-pointer flex justify-center items-center h-10 px-4 rounded border border-[#CBD5E1] text-sm font-semibold text-[#475569]"
            @click.stop="handleCancel"
            @mousedown.stop
          >
            Cancel
          </div>
          <div
            class="cursor-pointer flex justify-center items-center h-10 px-6 rounded bg-primary text-white text-sm font-semibold"
            @click.stop="handleApply"
            @mousedown.stop
          >
            Apply
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Dropdown from "./Dropdown.vue";
import Icon from "./Icon.vue";

export default {
  name: "CustomFilter",

  components: {
    Dropdown,
    Icon
  },

  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    filterDefinitions: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    error: {
      type: [String, Error, Object],
      default: null
    }
  },

  data() {
    return {
      isOpen: false,
      panelRef: null,
      toggleButtonRef: null,
      counter: 0,
      workingFilters: []
    };
  },

  watch: {
    modelValue: {
      handler(value) {
        this.syncFromModel(value);
      },
      immediate: true,
      deep: true
    },
    isOpen: function(open) {
      if (open && this.workingFilters.length === 0) {
        this.addFilter();
      }
      // Manage event listener based on panel state
      if (open) {
        document.addEventListener('mousedown', this.handleOutsideClick);
      } else {
        document.removeEventListener('mousedown', this.handleOutsideClick);
      }
    }
  },

  mounted() {
    // Event listener is now managed by the isOpen watcher
  },

  beforeDestroy() {
    // Clean up event listener
    document.removeEventListener('mousedown', this.handleOutsideClick);
  },

  methods: {
    syncFromModel(value) {
      const normalized = [];
      let maxId = 0;
      (value || []).forEach((filter, index) => {
        const id = typeof filter?.id === "number" ? filter.id : index + 1;
        maxId = Math.max(maxId, id);
        normalized.push({
          id,
          field: filter?.field ?? null,
          operator: filter?.operator ?? null,
          value: filter?.value ?? null
        });
      });
      this.counter = maxId;
      this.workingFilters = normalized;
    },

    togglePanel() {
      this.isOpen = !this.isOpen;
    },

    getDefinition(field) {
      return (
        this.filterDefinitions.find(definition => definition.key === field) ||
        null
      );
    },

    getOperatorsForFilter(field) {
      return this.getDefinition(field)?.operators || [];
    },

    getValueType(field) {
      return this.getDefinition(field)?.valueType || "text";
    },

    getValueOptions(field) {
      return this.getDefinition(field)?.options || [];
    },

    handleFieldChange(index, fieldKey) {
      const definition = this.getDefinition(fieldKey);
      const nextOperator = definition?.operators?.[0]?.value || null;
      const current = this.workingFilters[index];
      if (!current) {
        return;
      }
      this.workingFilters.splice(index, 1, {
        ...current,
        field: fieldKey || null,
        operator: nextOperator,
        value: null
      });
    },

    handleOperatorChange(index, operator) {
      const current = this.workingFilters[index];
      if (!current) {
        return;
      }
      this.workingFilters.splice(index, 1, {
        ...current,
        operator: operator || null,
        value: null
      });
    },

    updateFilterValue(index, value) {
      const current = this.workingFilters[index];
      if (!current) {
        return;
      }
      this.workingFilters.splice(index, 1, {
        ...current,
        value: value || null
      });
    },

    mapOperator(operator) {
      const operatorKeyMap = {
        is: "is",
        contains: "contains",
        is_not: "is_not",
        before: "before",
        after: "after",
        is_today: "is_today",
        is_yesterday: "is_yesterday",
        since_7_days: "since_7_days",
        since_last_30_days: "since_last_30_days",
        is_custom: "is_custom",
        is_less_than: "is_less_than",
        is_greater_than: "is_greater_than"
      };
      return operatorKeyMap[operator] || null;
    },

    buildFilterPayload(filters) {
      const payload = { filters: {} };
      filters.forEach(filter => {
        if (!filter.field || !filter.operator || !filter.value) {
          return;
        }
        const operatorKey = this.mapOperator(filter.operator);
        if (!operatorKey) {
          return;
        }
        const fieldKey = typeof filter.field === "string" ? filter.field : "";
        if (!fieldKey) {
          return;
        }
        if (!payload.filters[fieldKey]) {
          payload.filters[fieldKey] = {};
        }
        payload.filters[fieldKey][operatorKey] = filter.value;
      });
      return payload;
    },

    handleInputChangeEvent(index, event) {
      const target =
        event.target instanceof HTMLInputElement ? event.target : null;
      this.updateFilterValue(index, target?.value ?? "");
    },

    getFilterLabel(option) {
      return option.label;
    },

    getFilterKey(option) {
      return option.key;
    },

    handleFieldSelect(index, value) {
      let fieldKey = "";
      if (typeof value === "string") {
        fieldKey = value;
      } else if (
        value &&
        typeof value === "object" &&
        value !== null &&
        "key" in value
      ) {
        fieldKey = value.key;
      }
      this.handleFieldChange(index, fieldKey);
    },

    getOperatorLabel(option) {
      return option.label;
    },

    getOperatorValue(option) {
      return option.value;
    },

    handleOperatorSelect(index, value) {
      let operatorValue = "";
      if (typeof value === "string") {
        operatorValue = value;
      } else if (
        value &&
        typeof value === "object" &&
        value !== null &&
        "value" in value
      ) {
        operatorValue = value.value;
      }
      this.handleOperatorChange(index, operatorValue);
    },

    getValueOptionLabel(option) {
      return option.label;
    },

    getValueOptionValue(option) {
      return option.value;
    },

    handleValueSelect(index, value) {
      let resolvedValue = "";
      if (typeof value === "string") {
        resolvedValue = value;
      } else if (
        value &&
        typeof value === "object" &&
        value !== null &&
        "value" in value
      ) {
        resolvedValue = value.value;
      }
      this.updateFilterValue(index, resolvedValue);
    },

    addFilter() {
      const nextId = this.counter + 1;
      this.counter = nextId;
      this.workingFilters = [
        ...this.workingFilters,
        {
          id: nextId,
          field: null,
          operator: null,
          value: null
        }
      ];
    },

    removeFilter(index) {
      const updated = [...this.workingFilters];
      updated.splice(index, 1);
      this.workingFilters = updated;
    },

    handleCancel() {
      this.syncFromModel(this.modelValue);
      this.isOpen = false;
    },

    handleApply() {
      const nextFilters = this.workingFilters.map(filter => ({ ...filter }));
      this.$emit("update:modelValue", nextFilters);
      this.$emit("apply", this.buildFilterPayload(nextFilters));
      this.isOpen = false;
    },

    handleOutsideClick(event) {
      if (!this.isOpen) {
        return;
      }
      const target = event.target instanceof HTMLElement ? event.target : null;

      // Check if click is inside panel or button
      const isInsidePanel = this.panelRef?.contains(target);
      const isInsideButton = this.toggleButtonRef?.contains(target);

      if (isInsidePanel || isInsideButton) {
        return;
      }

      // Check if click is on dropdown options or related elements
      const isDropdownElement = target && (
        target.closest('[data-dropdown]') ||
        target.closest('.dropdown-menu') ||
        target.closest('.dropdown-option') ||
        target.closest('[role="option"]') ||
        target.closest('[role="listbox"]') ||
        target.closest('.core-dropdown') ||
        target.closest('[class*="dropdown"]') ||
        target.closest('[class*="core-dropdown"]')
      );

      if (isDropdownElement) {
        return;
      }

      // Small delay to allow dropdown events to process first
      this.$nextTick(() => {
        if (this.isOpen) {
          this.isOpen = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
